import React from "react";
import PathConstants from "./path-constants";
import { FormDirtyLayout } from "../components/navigation-blocker";
import { SectionLockGate } from "../components/section-lock-gate";
import { PaymentGate } from "../components/payment-gate";
import { SubmittedGate } from "../components/submitted-gate";

const MyAppealsList = React.lazy(() => import("../pages/my-appeals"));
const Account = React.lazy(() => import("../pages/my-appeals/account"));
const Eligibility = React.lazy(() => import("../pages/my-appeals/eligibility"));
const AppealDetail = React.lazy(() => import("../pages/my-appeals/appeal-id"));

const Insurance = React.lazy(
  () => import("../pages/my-appeals/appeal-id/insurance"),
);

const UploadInsuranceCard = React.lazy(
  () => import("../pages/my-appeals/appeal-id/insurance/upload-card"),
);
const PrimaryEnrollee = React.lazy(
  () => import("../pages/my-appeals/appeal-id/insurance/primary-enrollee"),
);

const DenyingParty = React.lazy(
  () => import("../pages/my-appeals/appeal-id/insurance/denying-party"),
);

const PatientInfo = React.lazy(
  () => import("../pages/my-appeals/appeal-id/patient-info"),
);

const DenialReason = React.lazy(
  () => import("../pages/my-appeals/appeal-id/denial-reason"),
);

const SubDenialReason = React.lazy(
  () => import("../pages/my-appeals/appeal-id/denial-reason/sub-denial-reason"),
);

const AlternativeCondition = React.lazy(
  () =>
    import("../pages/my-appeals/appeal-id/denial-reason/alternative-condition"),
);

const BmiCriteriaFollowUp = React.lazy(
  () =>
    import("../pages/my-appeals/appeal-id/denial-reason/bmi-criteria-follow-up"),
);

const OtherAppealSituations = React.lazy(
  () =>
    import("../pages/my-appeals/appeal-id/denial-reason/other-appeal-situations"),
);

const SurveyLegacy = React.lazy(
  () => import("../pages/my-appeals/appeal-id/survey-legacy"),
);

const Survey = React.lazy(() => import("../pages/my-appeals/appeal-id/survey"));

const Documents = React.lazy(
  () => import("../pages/my-appeals/appeal-id/documents"),
);

const PatientDestination = React.lazy(
  () => import("../pages/my-appeals/appeal-id/finalize/patient-destination"),
);

const EmployerDestination = React.lazy(
  () => import("../pages/my-appeals/appeal-id/finalize/employer-destination"),
);

const AppealDestination = React.lazy(
  () => import("../pages/my-appeals/appeal-id/finalize/appeal-destination"),
);

const CcDestinations = React.lazy(
  () => import("../pages/my-appeals/appeal-id/finalize/cc-destinations"),
);

const PrePayment = React.lazy(
  () => import("../pages/my-appeals/appeal-id/finalize/pre-payment"),
);

const ReadyToSubmit = React.lazy(
  () => import("../pages/my-appeals/appeal-id/finalize/ready-to-submit"),
);

const ReviewAppeal = React.lazy(
  () => import("../pages/my-appeals/appeal-id/finalize/review-appeal"),
);

const PurchaseComplete = React.lazy(
  () => import("../pages/my-appeals/appeal-id/finalize/purchase-complete"),
);

const GenerateLetter = React.lazy(
  () =>
    import("../pages/my-appeals/appeal-id/generate-and-review/generate-letter"),
);

const ReviewLetter = React.lazy(
  () =>
    import("../pages/my-appeals/appeal-id/generate-and-review/review-letter"),
);

const routes = [
  { path: PathConstants.MyAppeals, element: <MyAppealsList /> },
  { path: PathConstants.MyAppealsAccount, element: <Account /> },
  { path: PathConstants.MyAppealsEligibility, element: <Eligibility /> },
  { path: PathConstants.MyAppealsDetail, element: <AppealDetail /> },
  { path: PathConstants.MyAppealsInsurance, element: <Insurance /> },
  {
    path: PathConstants.MyAppealsInsuranceUploadCard,
    element: <UploadInsuranceCard />,
  },
  {
    path: PathConstants.MyAppealsInsurancePrimaryEnrollee,
    element: <PrimaryEnrollee />,
  },
  {
    path: PathConstants.MyAppealsInsuranceDenyingParty,
    element: <DenyingParty />,
  },
  {
    element: <FormDirtyLayout />,
    children: [
      {
        path: PathConstants.MyAppealsPatientInfo,
        element: <PatientInfo />,
      },
    ],
  },
  { path: PathConstants.MyAppealsDenialReason, element: <DenialReason /> },
  {
    path: PathConstants.MyAppealsDenialReasonSubDenialReason,
    element: <SubDenialReason />,
  },
  {
    path: PathConstants.MyAppealsDenialReasonAlternativeCondition,
    element: <AlternativeCondition />,
  },
  {
    path: PathConstants.MyAppealsDenialReasonBmiCriteriaFollowUp,
    element: <BmiCriteriaFollowUp />,
  },
  {
    path: PathConstants.MyAppealsDenialReasonOtherAppealSituations,
    element: <OtherAppealSituations />,
  },
  {
    element: <SectionLockGate key="surveySection" section="surveySection" />,
    children: [
      { path: PathConstants.MyAppealsSurveyLegacy, element: <SurveyLegacy /> },
      { path: PathConstants.MyAppealsSurvey, element: <Survey /> },
    ],
  },
  { path: PathConstants.MyAppealsDocuments, element: <Documents /> },
  {
    element: (
      <SectionLockGate
        key="finalizeAndSubmitSection"
        section="finalizeAndSubmitSection"
      />
    ),
    children: [
      {
        element: <PaymentGate />,
        children: [
          {
            path: PathConstants.MyAppealsFinalizePatientDestination,
            element: <PatientDestination />,
          },
          {
            path: PathConstants.MyAppealsFinalizeEmployerDestination,
            element: <EmployerDestination />,
          },
          {
            path: PathConstants.MyAppealsFinalizeAppealDestination,
            element: <AppealDestination />,
          },
          {
            path: PathConstants.MyAppealsFinalizeCcDestinations,
            element: <CcDestinations />,
          },
          {
            path: PathConstants.MyAppealsFinalizePrePayment,
            element: <PrePayment />,
          },
        ],
      },
      {
        element: <SubmittedGate />,
        children: [
          {
            path: PathConstants.MyAppealsFinalizeReadyToSubmit,
            element: <ReadyToSubmit />,
          },
        ],
      },
      {
        path: PathConstants.MyAppealsFinalizeReviewAppeal,
        element: <ReviewAppeal />,
      },
      {
        path: PathConstants.MyAppealsFinalizePurchaseComplete,
        element: <PurchaseComplete />,
      },
    ],
  },
  {
    element: (
      <SectionLockGate
        key="generateAndReviewSection"
        section="generateAndReviewSection"
      />
    ),
    children: [
      {
        path: PathConstants.MyAppealsGenerateLetter,
        element: <GenerateLetter />,
      },
      {
        path: PathConstants.MyAppealsGenerateReviewLetter,
        element: <ReviewLetter />,
      },
    ],
  },
];

export default routes;
